/*
 * gy-analytics.js — Gullye storefront custom telemetry emitter.
 *
 * Emits custom UI events via Shopify.analytics.publish(), which the GA4 custom
 * pixel (installed in Shopify admin -> Customer events) subscribes to and
 * forwards to GA4 (G-QPD3DC5THF). We DO NOT re-send Shopify's standard
 * ecommerce events (view_item, add_to_cart, begin_checkout, purchase) here.
 *
 * Every event carries a shared context bundle (page_type, template, device,
 * is_logged_in, traffic_type). Internal team traffic is flagged so GA4 can
 * exclude it: append ?gy_internal=1 on any page to mark this browser internal
 * (persisted in a cookie); ?gy_internal=0 clears it.
 *
 * Add window.GY_DEBUG = true in the console to log every event.
 */
(function () {
  'use strict';

  function cookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? m[2] : '';
  }

  // ----- internal-traffic gate -----
  try {
    var qs = new URLSearchParams(window.location.search);
    if (qs.get('gy_internal') === '1') {
      document.cookie = 'gy_internal=1; path=/; max-age=31536000; samesite=lax';
    } else if (qs.get('gy_internal') === '0') {
      document.cookie = 'gy_internal=; path=/; max-age=0; samesite=lax';
    }
  } catch (e) {}
  var TRAFFIC_TYPE = cookie('gy_internal') === '1' ? 'internal' : 'external';

  var body = document.body;
  function ctx() {
    var device = 'desktop';
    if (window.matchMedia('(max-width: 749px)').matches) device = 'mobile';
    else if (window.matchMedia('(max-width: 989px)').matches) device = 'tablet';
    return {
      page_type: body.getAttribute('data-gy-page-type') || 'unknown',
      template: body.getAttribute('data-gy-template') || '',
      page_path: window.location.pathname,
      // Real storefront URL captured in the page context. The GA4 pixel runs in a
      // sandboxed iframe whose own document.location is a /web-pixels@.../sandbox/…
      // URL; forwarding this authoritative href lets the pixel set GA4's
      // page_location correctly instead of leaking the sandbox path.
      page_location: window.location.href,
      device: device,
      is_logged_in: body.getAttribute('data-gy-logged-in') === 'true',
      traffic_type: TRAFFIC_TYPE
    };
  }

  // Label sanitizer (GAP-3): explicit data-gy-label wins; otherwise trimmed
  // visible text. Guard against script/module strings leaking in (a nav link
  // once logged `await import("//gullye.com/cdn/...")` as its label) — anything
  // over 80 chars or containing code-ish tokens becomes '(unlabeled)'.
  function cleanLabel(el, alt) {
    var explicit = el && el.getAttribute ? el.getAttribute('data-gy-label') : '';
    if (explicit) return explicit.trim().slice(0, 80);
    var raw = ((el && el.textContent) || alt || '').trim().replace(/\s+/g, ' ');
    var bad = !raw || raw.length > 80 || raw.indexOf('import(') > -1 ||
      raw.indexOf('<') > -1 || raw.indexOf('//') > -1 || raw.indexOf('function') > -1;
    return bad ? '(unlabeled)' : raw;
  }

  // ----- emit (queue until Shopify.analytics is ready) -----
  var queue = [];
  function flush() {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      while (queue.length) {
        var e = queue.shift();
        try { window.Shopify.analytics.publish(e.name, e.data); } catch (err) {}
      }
      return true;
    }
    return false;
  }
  function track(name, params) {
    var data = Object.assign({}, ctx(), params || {});
    queue.push({ name: name, data: data });
    if (!flush()) {
      var tries = 0;
      var iv = setInterval(function () { if (flush() || ++tries > 40) clearInterval(iv); }, 250);
    }
    if (window.GY_DEBUG) console.log('[gy]', name, data);
  }
  window.gyTrack = track;

  // ----- Klaviyo: identify + Added to Cart -----
  // Our custom fly-to-cart adds via fetch('/cart/add.js'); Klaviyo's automatic
  // Added-to-Cart detection misses it, so we fire the event ourselves off the
  // cartUpdate pub/sub (which carries the /cart/add.js response). The event only
  // helps the abandoned-cart flow if the profile is identified (has an email), so
  // we identify logged-in shoppers on load and on any waitlist/email capture.
  function gyKlaviyo() { return (window.klaviyo = window.klaviyo || []); }
  function gyIdentify(email) {
    if (!email) return;
    try { gyKlaviyo().push(['identify', { '$email': email }]); } catch (e) {}
  }
  window.gyIdentify = gyIdentify;
  gyIdentify(body.getAttribute('data-gy-email'));

  var GY_ADD_SOURCES = { 'product-form': 1, 'quick-add': 1, 'quick-add-bulk': 1 };
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        try {
          if (!event || !GY_ADD_SOURCES[event.source]) return;      // adds only, not cart-page qty edits
          var resp = event.cartData; if (!resp) return;
          var added = resp.items ? resp.items[0] : resp;
          if (!added || !added.product_id) return;
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            gyKlaviyo().push(['track', 'Added to Cart', {
              '$value': cart.total_price / 100,
              'AddedItemProductName': added.product_title,
              'AddedItemProductID': String(added.product_id),
              'AddedItemVariantID': String(added.variant_id),
              'AddedItemSKU': added.sku,
              'AddedItemImageURL': added.image,
              'AddedItemURL': location.origin + '/products/' + added.handle,
              'ItemNames': cart.items.map(function (i) { return i.product_title; }),
              'Items': cart.items.map(function (i) {
                return {
                  ProductName: i.product_title, ProductID: String(i.product_id),
                  VariantID: String(i.variant_id), Quantity: i.quantity,
                  ItemPrice: i.price / 100, RowTotal: i.line_price / 100,
                  ImageURL: i.image, ProductURL: location.origin + '/products/' + i.handle
                };
              }),
              'CheckoutURL': location.origin + '/cart'
            }]);
            if (window.GY_DEBUG) console.log('[gy] klaviyo Added to Cart', added.product_title);
          }).catch(function () {});
        } catch (e) { if (window.GY_DEBUG) console.warn('[gy] klaviyo ATC', e); }
      });
    }
  } catch (e) {}

  // ----- listeners -----

  // nav_menu_open (mobile hamburger drawer)
  var drawer = document.getElementById('Details-menu-drawer-container');
  if (drawer) {
    drawer.addEventListener('toggle', function () {
      if (drawer.open) track('nav_menu_open', { menu_type: 'hamburger' });
    });
  }

  document.addEventListener(
    'click',
    function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;

      // A product card is one big anchor, so its own CONTROLS — gallery arrows,
      // the quick-add +/- stepper, the waitlist bell — sit inside that anchor.
      // Using one of them is not a click THROUGH to the product, but this
      // listener runs in the CAPTURE phase and therefore sees the event before
      // the controls can stopPropagation. Without this guard an arrow tap was
      // counted twice over: once as plp_card_click and once by the cta_click
      // catch-all, so simply looking at a second photo inflated both the card
      // click column and PLP page-click rate. The controls' own events
      // (plp_quick_add, plp_card_image_flip) are emitted from gy-cards.js and
      // are unaffected by this.
      if (t.closest('[data-gy-cardimg-prev], [data-gy-cardimg-next], .gy-qk, [data-gy-add], [data-gy-step], [data-gy-inc], [data-gy-dec], [data-gy-bell]')) return;

      // nav_logo_click — header logo (fires on every page, incl. home)
      var logo = t.closest('.header__heading-link');
      if (logo) {
        track('nav_logo_click', {
          item_location: 'header',
          item_label: 'logo',
          destination: logo.getAttribute('href') || '/'
        });
        return;
      }

      // search_open — header search toggle, opening only (results render is
      // the existing view_search_results)
      var searchT = t.closest('.header__icon--search');
      if (searchT) {
        var sd = searchT.closest('details');
        if (!sd || !sd.open) track('search_open', { item_location: 'header' });
        return;
      }

      // plp_filter_apply — chapter/material chips: mobile editorial PLP
      // (.gy-col__chip buttons) + desktop PLP (.gy-plpd__chip anchors).
      // Returns so desktop chips don't also fall into cta_click.
      // .gy-chip is the canonical pill (assets/gy-chips.css), used by search;
      // the two older class names are the section-scoped copies still in
      // gy-collection and gy-plp-desktop. plp_filter_apply is already in the
      // pixel allowlist, so this needs no re-paste.
      var chip = t.closest('.gy-col__chip, .gy-plpd__chip, .gy-chip');
      if (chip) {
        track('plp_filter_apply', {
          filter_type: chip.closest('.gy-sr__chips') ? 'search' : 'chapter',
          filter_value: cleanLabel(chip)
        });
        return;
      }

      // checkout_click — cart page + drawer checkout button
      var co = t.closest('button[name="checkout"], .cart__checkout-button');
      if (co) {
        track('checkout_click', {
          item_location: co.closest('#CartDrawer, cart-drawer') ? 'drawer' : 'cart_page'
        });
        return;
      }

      // express_checkout_click — dynamic wallet container. Best effort: the
      // provider buttons render in iframes/shadow DOM, so the provider is
      // usually unknowable here and some taps never bubble out at all.
      var express = t.closest('.additional-checkout-buttons, .cart__dynamic-checkout-buttons');
      if (express) {
        track('express_checkout_click', {
          item_location: express.closest('#CartDrawer, cart-drawer') ? 'drawer' : 'cart_page',
          provider: '(unknown)',
          cta_label: '(wallet)'
        });
        return;
      }

      // continue_shopping_click — cart title-row link + empty-cart button
      var cont = t.closest('cart-items .title-wrapper-with-link a, .cart__warnings > a.button');
      if (cont) {
        track('continue_shopping_click', { destination: cont.getAttribute('href') || '' });
        return;
      }

      // cart_qty_change — quantity steppers, cart page/drawer only (the PDP
      // quantity selector uses the same button names; excluded by scope check)
      var qty = t.closest('button[name="plus"], button[name="minus"]');
      if (qty && qty.closest('cart-items, cart-drawer-items, #CartDrawer, .cart-item')) {
        var dirUp = qty.getAttribute('name') === 'plus';
        track('cart_qty_change', { direction: dirUp ? 'up' : 'down', item_label: dirUp ? 'qty_up' : 'qty_down' });
        return;
      }

      // nav_menu_click — drawer, desktop inline menu, footer menu
      var ml = t.closest('#menu-drawer a, .header__inline-menu a, .footer-block__details-content a');
      if (ml && ml.getAttribute('href')) {
        var location = ml.closest('#menu-drawer')
          ? 'drawer'
          : ml.closest('footer')
          ? 'footer'
          : 'header';
        track('nav_menu_click', {
          item_label: cleanLabel(ml),
          // .gy-msub = the drawer's inline expansion panels (v3 register menu);
          // .menu-drawer__submenu = Dawn's slide-over panels (desktop drawer mode)
          item_level: ml.closest('.menu-drawer__submenu, .gy-msub') ? 'sub' : 'top',
          item_location: location,
          destination: ml.getAttribute('href')
        });
        return;
      }

      // recently_viewed_click — a card in the Recently viewed rail. Fires
      // INSTEAD of plp_card_click (returns first) so one tap = one event, and so
      // the rail's clicks don't inflate PLP card CTR. Pairs with
      // recently_viewed_view (in gy-recently-viewed.liquid) to give the rail CCR.
      var rvLink = t.closest('[data-gy-rv] [data-gy-card] a, [data-gy-rv] a[data-gy-card]');
      if (rvLink) {
        var rvCard = rvLink.closest('[data-gy-card]');
        var rvRail = rvLink.closest('[data-gy-rv]');
        var rvCells = rvRail.querySelectorAll('[data-gy-card]');
        track('recently_viewed_click', {
          section_name: 'gy_recently_viewed',
          item_location: body.getAttribute('data-gy-page-type') || 'unknown',
          product_id: rvCard.getAttribute('data-gy-item-id'),
          product_name: rvCard.getAttribute('data-gy-item-name'),
          list_position: Array.prototype.indexOf.call(rvCells, rvCard) + 1,
          destination: rvLink.getAttribute('href') || ''
        });
        return;
      }

      // plp_card_click — product card in a collection/home row
      // Match a link inside a card wrapper OR a card that *is* the link
      // (anchor-as-card, e.g. rails/related rows). closest() then resolves the
      // nearest [data-gy-card] either way.
      var cardLink = t.closest('[data-gy-card] a, a[data-gy-card]');
      if (cardLink) {
        var card = cardLink.closest('[data-gy-card]');
        var listRoot = card.closest('ul, .grid, .slider, .collection, .gy-rail');
        var cards = listRoot ? listRoot.querySelectorAll('[data-gy-card]') : [card];
        var pos = Array.prototype.indexOf.call(cards, card) + 1;
        var cardSec = card.closest('.shopify-section');
        var rawCardSecId = cardSec ? (cardSec.id || '') : '';
        // Same normalization as content_section_view / cta_click ('__' split),
        // so section_name joins cleanly across all three events.
        var cardSecId = rawCardSecId.indexOf('__') > -1
          ? rawCardSecId.split('__').pop()
          : rawCardSecId.replace('shopify-section-', '');
        var cardSecHeading = cardSec ? cardSec.querySelector('h2, h3, .collection-list__title, .section-header__title, .title') : null;
        var cardSecLabel = cardSecHeading ? cardSecHeading.textContent.trim().replace(/\s+/g, ' ').slice(0, 60) : '';
        // "Seen with" product chips: name the strip, not the section hosting it.
        // On a PDP the strip sits inside gy_mobile_pdp / gy_pdp_desktop, so the
        // generic section lookup would file every tap under the product page.
        // Position is the tile (video/photo) the chip sits on, which the rail
        // selector above can't see.
        var swRoot = card.closest('[data-gy-sw]');
        if (swRoot) {
          cardSecId = swRoot.getAttribute('data-gy-sw-key') || cardSecId;
          cardSecLabel = 'Seen with';
          var swTile = card.closest('.gy-sw__tile');
          pos = Array.prototype.indexOf.call(swRoot.querySelectorAll('.gy-sw__tile'), swTile) + 1;
        }
        var pathMatch = window.location.pathname.match(/\/collections\/([^/?#]+)/);
        track('plp_card_click', {
          product_id: card.getAttribute('data-gy-item-id'),
          product_name: card.getAttribute('data-gy-item-name'),
          collection: body.getAttribute('data-gy-collection') || cardSecId,
          collection_handle: pathMatch ? pathMatch[1] : '',
          section_id: cardSecId,
          section_name: cardSecId,
          section_label: cardSecLabel,
          list_position: pos
        });
        return;
      }

      // trust_click — shipping / returns / craft / faq / pre-order links
      var trust = t.closest(
        'a[href*="/pages/shipping"], a[href*="/pages/returns"], a[href*="/pages/our-craft"], a[href*="/pages/faq"], a[href*="/pages/pre-order"]'
      );
      if (trust) {
        var href = trust.getAttribute('href') || '';
        var target =
          href.indexOf('shipping') > -1 ? 'shipping'
          : href.indexOf('returns') > -1 ? 'returns'
          : href.indexOf('our-craft') > -1 ? 'craft'
          : href.indexOf('faq') > -1 ? 'faq'
          : href.indexOf('pre-order') > -1 ? 'preorder'
          : 'other';
        track('trust_click', { trust_target: target });
        return;
      }

      // cart_open — header cart trigger
      var cartT = t.closest('#cart-icon-bubble, a[href$="/cart"], .header__icon--cart');
      if (cartT) {
        track('cart_open', {});
        return;
      }

      // pdp_media_interact — zoom / thumbnail / media click on the product page
      var media = t.closest('.product__modal-opener, .product__media-toggle, .product__media-item, .thumbnail');
      if (media && t.closest('.product, media-gallery')) {
        var mAction = t.closest('.product__modal-opener, .product__media-toggle')
          ? 'zoom'
          : t.closest('.thumbnail')
          ? 'thumbnail'
          : 'view';
        track('pdp_media_interact', { media_action: mAction, product_id: productId() });
        return;
      }

      // pdp_detail_open — product accordion opened (fires on open only)
      var summary = t.closest('.product summary, .accordion summary, .product__accordion summary');
      if (summary) {
        var det = summary.closest('details');
        if (det && !det.open) {
          track('pdp_detail_open', {
            detail_section: (summary.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60),
            product_id: productId()
          });
        }
        return;
      }

      // cart_item_remove
      var rm = t.closest('cart-remove-button, .cart-remove-button');
      if (rm) {
        var liName = '';
        var li = rm.closest('.cart-item, tr, .cart-drawer__item');
        if (li) {
          var nm = li.querySelector('.cart-item__name, .cart-item__details a, a');
          if (nm) liName = (nm.textContent || '').trim().slice(0, 80);
        }
        track('cart_item_remove', { product_name: liName });
        return;
      }

      // cta_click — catch-all click-out for any in-content link not handled
      // above (collection tiles, hero slides, "view all", story/editorial
      // links, breadcrumbs). Product cards are already covered by
      // plp_card_click, nav/cart/trust by their own handlers — each returns
      // first, so this only sees what's left. Fires on every key page so CTR
      // per module is uniform; section_id is normalized to match the
      // section_name emitted by content_section_view for a clean join.
      var CTA_PAGES = { index: 1, home: 1, collection: 1, product: 1, 'list-collections': 1, search: 1 };
      if (CTA_PAGES[body.getAttribute('data-gy-page-type')]) {
        var anchor = t.closest('#MainContent a[href]');
        if (anchor) {
          var href = anchor.getAttribute('href') || '';
          if (href && href.charAt(0) !== '#' && href.indexOf('mailto:') !== 0 && href.indexOf('tel:') !== 0) {
            var sec = anchor.closest('.shopify-section');
            var rawSecId = sec ? (sec.id || '') : '';
            var secId = rawSecId.indexOf('__') > -1
              ? rawSecId.split('__').pop()
              : rawSecId.replace('shopify-section-', '');
            var secHeading = sec ? sec.querySelector('h2, h3, .collection-list__title, .section-header__title, .title') : null;
            var secLabel = secHeading ? secHeading.textContent.trim().replace(/\s+/g, ' ').slice(0, 60) : '';
            var rawLabel = cleanLabel(anchor, anchor.getAttribute('aria-label'));
            var ctaType = 'link';
            if (anchor.classList.contains('button') || anchor.closest('.button-wrapper, .collection-list__view-all')) ctaType = 'button';
            else if (href.match(/\/collections\/[^/?#]+\/?([?#]|$)/) && !href.match(/\/products\//)) ctaType = 'collection_tile';
            else if (href.match(/\/products\//)) ctaType = 'product_card';
            track('cta_click', {
              cta_type: ctaType,
              cta_label: rawLabel,
              destination: href,
              section_id: secId,
              section_name: secId,
              section_label: secLabel
            });
          }
        }
      }
    },
    true
  );

  function productId() {
    var el = document.querySelector('[data-product-id]');
    return el ? el.getAttribute('data-product-id') : '';
  }

  // change events: variant select, collection filters, sort
  document.addEventListener('change', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;

    // plp_sort_change
    if (t.id === 'SortBy' || t.closest('#SortBy')) {
      track('plp_sort_change', { sort_value: t.value });
      return;
    }
    // plp_filter_apply (only when a facet is selected)
    var ff = t.closest('#FacetFiltersForm, .facets__form, .facets__form-vertical, facet-filters-form');
    if (ff && (t.type === 'checkbox' || t.type === 'radio')) {
      if (t.checked) track('plp_filter_apply', { filter_type: (t.name || '').replace('filter.', ''), filter_value: t.value });
      return;
    }
    // pdp_variant_select
    if (t.closest('variant-selects, variant-radios, .product-form__input')) {
      var vlabel = '';
      if (t.tagName === 'SELECT') vlabel = t.value;
      else if (t.type === 'radio' || t.type === 'checkbox') {
        vlabel = t.value || (t.nextElementSibling && t.nextElementSibling.textContent) || '';
      }
      track('pdp_variant_select', { product_id: productId(), variant: (vlabel || '').trim().slice(0, 80) });
      return;
    }
  });

  // content_section_view — fired once per module when it reaches the central
  // band of the viewport. Runs on every key page (home, collections, the
  // all-products / list-collections pages, PDPs, search) so it serves as the
  // CTR denominator for cta_click / plp_card_click everywhere, not just home.
  (function () {
    var pageType = body.getAttribute('data-gy-page-type');
    var TRACKED = { index: 1, home: 1, collection: 1, product: 1, 'list-collections': 1, search: 1 };
    if (!TRACKED[pageType]) return;
    if (!('IntersectionObserver' in window)) return;
    var seen = {};
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          // Fire when the section reaches the central band of the viewport.
          // rootMargin shrinks the root to the middle 40%, so this triggers
          // regardless of how tall the section is (full-height slideshows
          // could never reach a 0.5 ratio).
          if (e.isIntersecting) {
            var id = e.target.id || '';
            var key = id.indexOf('__') > -1 ? id.split('__').pop() : id.replace('shopify-section-', '');
            if (key && !seen[key]) {
              seen[key] = 1;
              track('content_section_view', { section_name: key });
              io.unobserve(e.target);
            }
          }
        });
      },
      { rootMargin: '-30% 0px -30% 0px', threshold: 0 }
    );
    document.querySelectorAll('#MainContent .shopify-section').forEach(function (sec) { io.observe(sec); });
  })();

  // ----- plp_card_view: the impression denominator -----
  // Fires once per product card per pageview, when the card actually reaches the
  // viewport. Without it there is no honest denominator for card engagement:
  // Shopify's GA4 integration sends no view_item_list, so a product with no
  // clicks is indistinguishable between "nobody wanted it" and "nobody ever
  // scrolled far enough to see it". Deduped per product so one shopper scrolling
  // up and down a grid counts once.
  (function () {
    if (!('IntersectionObserver' in window)) return;
    var seen = {};
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var card = e.target;
          io.unobserve(card);
          var id = card.getAttribute('data-gy-item-id') || '';
          var name = card.getAttribute('data-gy-item-name') || '';
          var key = id || name;
          if (!key || seen[key]) return;
          seen[key] = 1;
          var m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
          track('plp_card_view', {
            product_id: id,
            product_name: name,
            collection_handle: m ? m[1] : ''
          });
        });
      },
      // Half the card must be on screen — a card clipped at the fold has not
      // really been seen, and counting it would inflate the denominator.
      { threshold: 0.5 }
    );
    function observeCards(ctx) {
      (ctx || document).querySelectorAll('[data-gy-card]').forEach(function (c) { io.observe(c); });
    }
    observeCards();
    // Card lists injected later (recently-viewed rail, PDP "keep exploring").
    window.gyObserveCards = observeCards;
  })();
})();
